import { type ChangeEvent, useEffect, useMemo } from "react";

import {
  type CloudFlowNodeType,
  type GCPNodeConfigApiParameters,
  type Member,
  type StructureApiServiceModelDescriptor,
} from "@doitintl/cmp-models";
import {
  Box,
  FormControl,
  FormControlLabel,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useFormikContext } from "formik";

import { cloudflowTexts } from "../../../../assets/texts";
import VirtualizedAutocomplete from "../../../../Components/Autocomplete/VirtualizedAutocomplete";
import { useCustomerId } from "../../../../Components/hooks/useCustomerId";
import { useGetGcpProjects } from "../../hooks";
import { type GCPPermissionConfigValues } from "../ConfigurationPanel/hooks";
import { useNodeConfigurationContext } from "../ConfigurationPanel/NodeConfigurationContext";
import {
  GCPHierarchicalResource,
  parseServiceAccountResource,
  PERMISSIONS_PROJECT_ID_KEY,
  PROJECT_ID_VARIATIONS,
} from "../utils/gcpUtils";

interface GCPPermissionFormProps {
  readonly inputModel: StructureApiServiceModelDescriptor<Member>;
  readonly resetPermissions: () => void;
}

const GCPPermissionForm = ({ inputModel, resetPermissions }: GCPPermissionFormProps) => {
  const { values, setFieldValue } = useFormikContext<GCPPermissionConfigValues>();
  const { nodeConfig, setActiveTab } = useNodeConfigurationContext<CloudFlowNodeType.ACTION>();
  const customerId = useCustomerId();

  const paramsProjectIdKey = useMemo(
    () => Object.keys(inputModel.members ?? {}).find((field) => PROJECT_ID_VARIATIONS.includes(field)),
    [inputModel]
  );

  const serviceAccountResource =
    (nodeConfig.parameters as GCPNodeConfigApiParameters).configurationValues.serviceAccountResource || "";

  const [resourceType, resourceId] = parseServiceAccountResource(serviceAccountResource);
  const organization = resourceType === "organizations" ? `organizations/${resourceId}` : "";

  const { data: projects, isLoading: gcpProjectsLoading } = useGetGcpProjects(customerId, organization, !!organization);

  const projectIds = useMemo(() => projects?.map((proj) => proj.projectId).filter(Boolean) || [], [projects]);

  const displayName = !serviceAccountResource
    ? "Not selected"
    : resourceType === "organizations"
      ? `Organization: ${resourceId}`
      : `Project: ${resourceId}`;

  const hierarchicalResource = values.hierarchicalResource || GCPHierarchicalResource.ORGANIZATION;
  const permissionsProjectId = paramsProjectIdKey
    ? (values[paramsProjectIdKey] ?? null)
    : values[PERMISSIONS_PROJECT_ID_KEY];

  const handleProjectIdChange = (value: string) => {
    setFieldValue(PERMISSIONS_PROJECT_ID_KEY, value);
    resetPermissions();
  };

  const handleHierarchicalResourceChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newResource = event.target.value as GCPHierarchicalResource;
    setFieldValue("hierarchicalResource", newResource);
    if (newResource === GCPHierarchicalResource.ORGANIZATION) {
      handleProjectIdChange("");
    }
    resetPermissions();
  };

  useEffect(() => {
    if (!values.hierarchicalResource) {
      setFieldValue("hierarchicalResource", GCPHierarchicalResource.ORGANIZATION);
    }
  }, [setFieldValue, values.hierarchicalResource]);

  return (
    <Stack sx={{ gap: 2 }}>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
          {cloudflowTexts.PERMISSIONS.CHECK_PERMISSIONS}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
          {cloudflowTexts.PERMISSIONS.CHECK_PERMISSIONS_DESCRIPTION}
        </Typography>
      </Box>

      <Stack spacing={2}>
        <Stack direction="row" gap={1}>
          <Typography variant="body2">Selected resource:</Typography>
          <Typography variant="subtitle2">{displayName}</Typography>
        </Stack>

        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
            {cloudflowTexts.PERMISSIONS.PERMISSION_CHECK_ON}
          </Typography>
          <RadioGroup
            style={{ marginLeft: 8 }}
            name="hierarchicalResource"
            value={hierarchicalResource}
            onChange={handleHierarchicalResourceChange}
          >
            <FormControlLabel
              value={GCPHierarchicalResource.ORGANIZATION}
              control={<Radio size="small" />}
              label={<Typography variant="body2">Organization</Typography>}
              disabled={resourceType !== "organizations"}
            />
            <FormControlLabel
              value={GCPHierarchicalResource.PROJECT}
              control={<Radio size="small" />}
              label={<Typography variant="body2">Project</Typography>}
            />
          </RadioGroup>
        </Box>

        {organization && hierarchicalResource === GCPHierarchicalResource.PROJECT && (
          <FormControl fullWidth>
            <VirtualizedAutocomplete
              id="project"
              loading={gcpProjectsLoading}
              options={projectIds}
              value={permissionsProjectId || null}
              onChange={(_, newValue) => {
                handleProjectIdChange(newValue as string);
              }}
              renderOption={(props, option) => (
                <MenuItem {...props} value={option}>
                  {option}
                </MenuItem>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  type="text"
                  label="Project ID"
                  name="Project"
                  fullWidth
                  variant="outlined"
                  required
                  helperText="Select a project for permission validation"
                />
              )}
              size="small"
              fullWidth
            />
          </FormControl>
        )}
      </Stack>

      {!serviceAccountResource && (
        <Typography variant="body2" color="text.secondary">
          Please select an organization or project in the{" "}
          <Link
            variant="body2"
            underline="hover"
            onClick={() => {
              setActiveTab("Parameters");
            }}
            style={{ cursor: "pointer" }}
          >
            {cloudflowTexts.PERMISSIONS.PARAMETERS_TAB}
          </Link>
        </Typography>
      )}
    </Stack>
  );
};

export default GCPPermissionForm;
